/**
 * 地図で選んだ場所のポップアップ
 *
 * 同じ場所が複数のラリーに登録されていることがあるため、場所を主役にしてラリーを並べる。
 * ピンをタップした時点では「見たい」のか「ルートに入れたい」のか分からないので、
 * 選択はこのポップアップのボタンからだけ行う。
 */

import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { Check, Navigation, Plus, X } from 'lucide-react'
import { useNavigate } from 'react-router'

import type { PlanRef } from '@/api/types/route-plan'
import type { RallyMapItem } from '@/api/types/stamp-rally'
import { StampFace } from '@/components/shared/stamp-face'
import { Button } from '@/components/ui/button'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'

/** 同じ座標のスポットをまとめた単位。items はラリーをまたぐ */
export type RallyPlaceGroup = {
  placeId: number
  name: string
  address: string | null
  position: google.maps.LatLngLiteral
  items: RallyMapItem[]
  /** この場所を既に入れている他のプラン。いま編集中のプランは含まない */
  plans: PlanRef[]
}

/** 吹き出しの下端を座標に合わせる（anchorLeft/anchorTop はコンテンツをずらす量なので負値） */
const ANCHOR_LEFT = '-50%'
const ANCHOR_TOP = '-100%'
const POPUP_Z_INDEX = 9999

const FALLBACK_COLOR = '#F97316'

const stampedLabel = (stampedAt: string): string =>
  parseUtcDate(stampedAt).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })

/** スポット 1 件を Google マップで開く URL。place_id があれば店舗ページが直接開く */
const mapsUrl = (place: RallyPlaceGroup): string => {
  const params = new URLSearchParams({ api: '1', query: `${place.position.lat},${place.position.lng}` })
  const googlePlaceId = place.items.find((item) => item.google_place_id)?.google_place_id
  if (googlePlaceId) params.set('query_place_id', googlePlaceId)
  return `https://www.google.com/maps/search/?${params.toString()}`
}

type Props = {
  place: RallyPlaceGroup
  selected: boolean
  onToggleSelect: () => void
  onToggleStamp: (item: RallyMapItem, stamped: boolean) => void
  onClose: () => void
  /** ラリー行を押したときの動き。省略するとそのラリーの詳細へ飛ぶ */
  onSelectItem?: (item: RallyMapItem) => void
}

export const RallyPlacePopup = ({ place, selected, onToggleSelect, onToggleStamp, onClose, onSelectItem }: Props) => {
  const navigate = useNavigate()

  return (
    <AdvancedMarker
      position={place.position}
      anchorLeft={ANCHOR_LEFT}
      anchorTop={ANCHOR_TOP}
      zIndex={POPUP_Z_INDEX}
      clickable
    >
      {/* 下の余白でピンを隠さないようにする */}
      <div className="w-64 pb-3">
        <div className="overflow-hidden rounded-xl border bg-background text-left shadow-lg">
          <div className="flex items-start gap-1 border-b px-3 py-2">
            <p className="min-w-0 flex-1 text-sm leading-snug font-medium">{place.name}</p>
            <button onClick={onClose} aria-label="閉じる" className="-mr-1 shrink-0 p-1 text-muted-foreground">
              <X size={14} />
            </button>
          </div>

          {place.address && <p className="px-3 pt-2 text-xs leading-snug text-muted-foreground">{place.address}</p>}

          <ul className="space-y-1.5 px-3 py-2">
            {place.items.map((item) => {
              const color = item.rally_color ?? FALLBACK_COLOR
              const stamped = item.stamped_at !== null
              return (
                <li key={item.id}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onToggleStamp(item, !stamped)}
                      aria-label={`${item.rally_title} のスタンプを${stamped ? '解除' : '押す'}`}
                    >
                      <StampFace size="sm" stamped={stamped} />
                    </button>
                    <button
                      onClick={() =>
                        onSelectItem
                          ? onSelectItem(item)
                          : navigate(PATHS.RALLIES.DETAIL.replace(':rally_id', String(item.rally_id)))
                      }
                      className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                    >
                      <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                      <span className="min-w-0 flex-1 truncate text-xs">{item.rally_title}</span>
                    </button>
                    {item.stamped_at && (
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                        {stampedLabel(item.stamped_at)}
                      </span>
                    )}
                  </div>
                  {item.note && <p className="pl-9 text-[10px] leading-snug text-muted-foreground">{item.note}</p>}
                </li>
              )
            })}
          </ul>

          {place.plans.length > 0 && (
            <p className="border-t px-3 py-1.5 text-[10px] leading-snug text-muted-foreground">
              {place.plans.map((plan) => plan.title).join('、')} に追加済み
            </p>
          )}

          <div className="flex gap-2 border-t px-3 py-2">
            <Button variant="outline" size="sm" className="shrink-0" asChild>
              <a href={mapsUrl(place)} target="_blank" rel="noreferrer" aria-label="Google マップで開く">
                <Navigation className="size-4" />
              </a>
            </Button>
            <Button
              size="sm"
              variant={selected ? 'secondary' : 'default'}
              className="flex-1 text-xs"
              onClick={onToggleSelect}
            >
              {selected ? <Check className="size-4" /> : <Plus className="size-4" />}
              {selected ? '選択中' : 'ルートに追加'}
            </Button>
          </div>
        </div>
      </div>
    </AdvancedMarker>
  )
}
